import { useMemo, useState } from "react";
import { useApp } from "@/store/AppStore";
import SectionTitle from "@/components/base/SectionTitle";
import Modal from "@/components/base/Modal";
import { ghostButtonClass } from "@/components/base/fieldStyles";
import CatalogVisual from "@/components/feature/CatalogVisual";
import CatalogStats from "@/pages/admin/catalogo/components/CatalogStats";
import ProductTable from "@/pages/admin/catalogo/components/ProductTable";
import ProductFormModal from "@/pages/admin/catalogo/components/ProductFormModal";
import CategoryManager from "@/pages/admin/catalogo/components/CategoryManager";
import type { Product } from "@/types";
import { resolveCategories } from "@/utils/categories";
import { formatCOP } from "@/utils/format";

type CatalogView = "visual" | "edit" | "categories";

const CatalogPage = () => {
  const { data, upsertProduct, deleteProduct, updateSettings } = useApp();
  const [view, setView] = useState<CatalogView>("visual");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("Todas");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);
  const [focused, setFocused] = useState(false);
  const [feedback, setFeedback] = useState("");

  const categories = useMemo(
    () => resolveCategories(data.settings.categories),
    [data.settings.categories]
  );
  const categoryFilters = useMemo(() => ["Todas", ...categories], [categories]);
  const categoryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    data.products.forEach((p) => {
      map[p.category] = (map[p.category] ?? 0) + 1;
    });
    return map;
  }, [data.products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.products.filter((p) => {
      const matchCategory = category === "Todas" || p.category === category;
      const matchQuery =
        !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q);
      return matchCategory && matchQuery;
    });
  }, [data.products, query, category]);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return data.products
      .filter((p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
      .slice(0, 6);
  }, [data.products, query]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditing(product);
    setFormOpen(true);
  };

  const handleSubmit = (product: Product) => {
    upsertProduct(product);
    setFormOpen(false);
    setFeedback(editing ? "Producto actualizado en el catálogo." : "Producto agregado al catálogo.");
    setTimeout(() => setFeedback(""), 2200);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteProduct(deleting.id);
    setDeleting(null);
    setFeedback("Producto eliminado del catálogo.");
    setTimeout(() => setFeedback(""), 2200);
  };

  return (
    <div className="pb-24">
      <SectionTitle
        text="Catálogo Maestro Amway Colombia"
        subtitle="Explora el portafolio en vista visual o edítalo con SKU, puntos PV, precio costo base y precio público. El cálculo de puntos y márgenes es automático."
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div className="inline-flex items-center gap-1 bg-background-200/50 rounded-full p-1 w-fit">
          <button
            onClick={() => setView("visual")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
              view === "visual"
                ? "bg-accent-500/25 text-accent-100"
                : "text-foreground-600 hover:text-accent-200"
            }`}
          >
            <i className="ri-layout-grid-line" /> Vista visual
          </button>
          <button
            onClick={() => setView("edit")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
              view === "edit"
                ? "bg-accent-500/25 text-accent-100"
                : "text-foreground-600 hover:text-accent-200"
            }`}
          >
            <i className="ri-edit-2-line" /> Editar catálogo
          </button>
          <button
            onClick={() => setView("categories")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${
              view === "categories"
                ? "bg-accent-500/25 text-accent-100"
                : "text-foreground-600 hover:text-accent-200"
            }`}
          >
            <i className="ri-price-tag-3-line" /> Categorías
          </button>
        </div>

        {view === "edit" && (
          <button
            onClick={openNew}
            className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
          >
            <i className="ri-add-line" /> Nuevo producto
          </button>
        )}
      </div>

      <CatalogStats products={data.products} />

      {view === "visual" && (
        <div className="mt-5">
          <CatalogVisual
            products={data.products}
            inventory={data.inventory}
            categories={categories}
          />
        </div>
      )}

      {view === "edit" && (
        <>
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mt-5 mb-4">
            <div className="relative flex-1 max-w-md">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-accent-300">
                <i className="ri-search-line" />
              </span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                placeholder="Buscar por nombre o SKU..."
                className="w-full bg-background-100/70 border border-background-300/50 rounded-md pl-9 pr-3 py-2 text-sm text-foreground-950 placeholder:text-foreground-500 outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/25"
                aria-label="Buscar producto"
              />
              {focused && suggestions.length > 0 && (
                <div className="absolute z-30 mt-1 w-full glass-panel-strong rounded-md overflow-hidden animate-fade-up">
                  {suggestions.map((s) => (
                    <button
                      key={s.id || s.sku}
                      onMouseDown={() => setQuery(s.name)}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-background-200/60 cursor-pointer"
                    >
                      <span className="min-w-0">
                        <span className="block text-xs font-medium text-foreground-900 truncate">
                          {s.name}
                        </span>
                        <span className="block text-[10px] text-accent-200 font-mono">{s.sku}</span>
                      </span>
                      <span className="text-[11px] text-foreground-600 whitespace-nowrap">
                        {formatCOP(s.pricePublic)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {categoryFilters.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border cursor-pointer whitespace-nowrap transition-colors ${
                    category === c
                      ? "bg-accent-500/20 border-accent-500/40 text-accent-200"
                      : "border-background-300/50 text-foreground-600 hover:border-accent-500/30"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {feedback && (
            <div className="mb-3 rounded-md glass-panel border-accent-500/30 px-4 py-2.5 flex items-center gap-2 animate-fade-up">
              <i className="ri-checkbox-circle-line text-accent-300" />
              <p className="text-xs text-foreground-800">{feedback}</p>
            </div>
          )}

          <ProductTable products={filtered} onEdit={openEdit} onDelete={setDeleting} />
        </>
      )}

      {view === "categories" && (
        <div className="mt-5">
          <CategoryManager
            categories={categories}
            counts={categoryCounts}
            onSave={(next) => {
              updateSettings({ categories: next });
              setFeedback("Categorías actualizadas.");
              setTimeout(() => setFeedback(""), 2200);
            }}
          />
        </div>
      )}

      <ProductFormModal
        open={formOpen}
        product={editing}
        categories={categories}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Eliminar producto"
        icon="ri-delete-bin-line"
        size="sm"
        footer={
          <>
            <button onClick={() => setDeleting(null)} className={ghostButtonClass}>
              Cancelar
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-sm font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
            >
              <i className="ri-delete-bin-line" /> Eliminar
            </button>
          </>
        }
      >
        <p className="text-sm text-foreground-700">
          ¿Seguro que quieres eliminar{" "}
          <strong className="text-foreground-950">{deleting?.name}</strong> del catálogo? También se
          borrará su registro de inventario asociado.
        </p>
      </Modal>
    </div>
  );
};

export default CatalogPage;